// Exit tickets (decision 86): the Submit box's reflection questions are scored in the app, out of 10.
// Full credit = a real answer to every question AND every required (non-bonus) lesson check passed.
// Partial = the share met, rounded. Bonus checks never count against a student.
//
// Pure functions, no imports: the Worker scores at submit time, the Submit dialog gives live
// feedback with the same rule, and the export script renders the breakdown.

/** Fewest words a real answer needs (not counting filler like "I don't know"). */
export const MIN_ANSWER_WORDS = 5;
export const EXIT_TICKET_OUT_OF = 10;

export type AnswerProblem = "empty" | "short" | "filler" | "repeat" | "gibberish" | "copy";

export interface AnswerCheck {
  ok: boolean;
  problem: AnswerProblem | null;
  /** Words that count toward the 5. */
  words: number;
  /** A short, kind tip for the student (7th–8th grade reading level). */
  tip: string;
}

// Phrases that say "I have no answer". Removed before counting, so "I don't know why, but the score
// kept going up" still counts and "I don't know what to say" doesn't.
const FILLER = [
  "i do not know",
  "i don't know",
  "i dont know",
  "i dunno",
  "dunno",
  "idk",
  "idc",
  "no idea",
  "no clue",
  "not sure",
  "i'm not sure",
  "im not sure",
  "i forgot",
  "whatever",
  "n/a",
  "blah",
  "lol",
  "meh",
  "lorem ipsum",
];

// Bits of keyboard rows people mash ("asdf", "qwerty", "jkl;").
const MASH = ["asdf", "sdfg", "dfgh", "fghj", "ghjk", "hjkl", "jkl", "qwer", "wert", "erty", "rtyu", "tyui", "yuio", "uiop", "zxcv", "xcvb", "cvbn", "vbnm"];

const TIPS: Record<AnswerProblem | "ok", string> = {
  ok: "This counts!",
  empty: "Write your answer here.",
  short: "Add a few more words.",
  filler: "It's OK not to be sure. Tell what you tried or what you think happened.",
  repeat: "Try saying it without repeating the same words.",
  gibberish: "That looks like random letters. Tell what really happened.",
  copy: "Answer in your own words instead of copying the question.",
};

/** Lowercase words, apostrophes kept ("don't"); punctuation splits words. */
function words(text: string): string[] {
  return (
    text
      .toLowerCase()
      .replace(/[’‘]/g, "'")
      .match(/[\p{L}\p{N}][\p{L}\p{N}'/]*/gu) ?? []
  ).map((w) => w.replace(/'+$/, ""));
}

/** A token that isn't a word: mashed keys, one letter held down, or no vowels at all ("sdfghj"). */
function isGibberish(w: string): boolean {
  if (/\d/.test(w)) return false; // "4", "3rd", "10x" are fine
  if (/(.)\1{2,}/.test(w)) return true; // "aaaa", "zzz"
  if (MASH.some((m) => w.includes(m))) return true;
  if (w.length >= 4 && !/[aeiouy]/.test(w)) return true;
  return false;
}

/** Drops filler phrases (whole words only) so they don't count toward the word total. */
function withoutFiller(list: string[]): string[] {
  let text = ` ${list.join(" ")} `;
  // Longest phrases first, so "i'm not sure" goes before "not sure".
  for (const f of [...FILLER].sort((a, b) => b.length - a.length)) {
    while (text.includes(` ${f} `)) text = text.replace(` ${f} `, " ");
  }
  return text.trim() ? text.trim().split(/\s+/) : [];
}

/**
 * Is this a real answer? At least 5 words of the student's own, not filler ("idk", "nothing"),
 * mashed keys, the same word over and over, or the question copied back.
 */
export function checkAnswer(answer: string, question = ""): AnswerCheck {
  const result = (problem: AnswerProblem | null, n: number): AnswerCheck => ({ ok: problem === null, problem, words: n, tip: TIPS[problem ?? "ok"] });
  const all = words(answer);
  if (all.length === 0) return result("empty", 0);

  // The question pasted in doesn't count; neither do words that all come from the question.
  const q = words(question);
  let own = all;
  if (q.length >= 3) {
    const qText = ` ${q.join(" ")} `;
    const aText = ` ${all.join(" ")} `;
    if (aText.includes(qText)) own = aText.replace(qText, " ").trim().split(/\s+/).filter(Boolean);
    const qSet = new Set(q);
    if (own.length === 0 || own.every((w) => qSet.has(w))) return result("copy", 0);
  }

  const gib = own.filter(isGibberish);
  const real = withoutFiller(own.filter((w) => !isGibberish(w)));
  const distinct = new Set(real).size;
  if (real.length >= MIN_ANSWER_WORDS && (distinct < 3 || distinct / real.length < 0.35)) return result("repeat", distinct);
  if (real.length >= MIN_ANSWER_WORDS) return result(null, real.length);
  // Not enough words: say why in the kindest useful way.
  if (gib.length > 0 && gib.length >= real.length) return result("gibberish", real.length);
  if (own.length - gib.length > real.length) return result("filler", real.length);
  if (own.length >= MIN_ANSWER_WORDS && new Set(own).size < 3) return result("repeat", real.length);
  return result("short", real.length);
}

/** The live hint under an answer box: what to do next, or that it counts. */
export function answerHint(answer: string, question = ""): string {
  const c = checkAnswer(answer, question);
  if (c.problem === "short") {
    const more = MIN_ANSWER_WORDS - c.words;
    return more === 1 ? "Add 1 more word." : c.words === 0 ? "Add a few more words." : `Add ${more} more words.`;
  }
  return c.tip;
}

/** One lesson check (a grading hook result), as far as the score needs it. */
export interface ExitTicketCheck {
  id: string;
  label: string;
  pass: boolean;
  bonus?: boolean;
}

export interface ExitTicketScore {
  /** 0–10. */
  score: number;
  outOf: number;
  answers: { counted: number; total: number };
  checks: { passed: number; total: number };
  /** What kept it from full credit. */
  missing: {
    answers: { index: number; question: string; problem: AnswerProblem }[];
    checks: { id: string; label: string }[];
  };
}

/**
 * The exit-ticket score: round(10 × (real answers + required checks passed) / (questions + required
 * checks)). Nothing to check at all counts as full credit.
 */
export function scoreExitTicket(reflection: { question: string; answer: string }[], evidence: ExitTicketCheck[]): ExitTicketScore {
  const missingAnswers: ExitTicketScore["missing"]["answers"] = [];
  reflection.forEach((r, index) => {
    const c = checkAnswer(r.answer, r.question);
    if (!c.ok) missingAnswers.push({ index, question: r.question, problem: c.problem! });
  });
  const required = evidence.filter((e) => !e.bonus);
  const failed = required.filter((e) => !e.pass).map((e) => ({ id: e.id, label: e.label }));
  const total = reflection.length + required.length;
  const met = reflection.length - missingAnswers.length + required.length - failed.length;
  const score = total === 0 ? EXIT_TICKET_OUT_OF : Math.round((EXIT_TICKET_OUT_OF * met) / total);
  return {
    score,
    outOf: EXIT_TICKET_OUT_OF,
    answers: { counted: reflection.length - missingAnswers.length, total: reflection.length },
    checks: { passed: required.length - failed.length, total: required.length },
    missing: { answers: missingAnswers, checks: failed },
  };
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "Exit ticket 8/10 · missing: 1 answer, 1 check" (or "· full credit"). */
export function exitTicketLine(s: ExitTicketScore): string {
  const parts = [
    ...(s.missing.answers.length ? [plural(s.missing.answers.length, "answer")] : []),
    ...(s.missing.checks.length ? [plural(s.missing.checks.length, "lesson check")] : []),
  ];
  return `Exit ticket ${s.score}/${s.outOf} · ${parts.length ? `missing: ${parts.join(", ")}` : "full credit"}`;
}
